import { BaseCompany } from '@/DTOS/Company/BaseCompany';
import { InterventionFileSystemType } from '@/DTOS/InterventionFile/InterventionFileSystemType';
import { WorkPlace } from '@/DTOS/WorkPlace/WorkPlace';
import { useHandleResponse } from '@/utils/useHandleResponse';
import { DeleteOutlined, EditOutlined, UploadOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  App,
  Button,
  Checkbox,
  Col,
  Collapse,
  Form,
  Image,
  Input,
  InputNumber,
  List,
  Row,
  Select,
  Spin,
  Upload,
} from 'antd';
import TextArea from 'antd/es/input/TextArea';
import Title from 'antd/es/typography/Title';
import dayjs from 'dayjs';
import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import SignaturePad from '../../../components/SignaturePad/SignaturePad';
import { useAuth } from '../../../context/AuthProvider';
import { setCompanies } from '../../../contextRedux/Company/CompanyStore';
import {
  addInterventionFileEquipments,
  deleteInterventionFileEquipmentsByIndex,
  initialState,
  setInterventionFile,
  updateField,
  updateInterventionFileEquipments,
  updatePerformOperation,
} from '../../../contextRedux/InterventionFile/InterventionFileModel';
import { setRequestFileStatuses } from '../../../contextRedux/RequestFileStatus/RequestFileStatusStore';
import { setSystemTypes } from '../../../contextRedux/SystemType/SystemTypeStore';
import { setUsers } from '../../../contextRedux/User/UserStore';
import AgentService from '../../../services/agent/AgentService';
import CompanyService from '../../../services/company/CompanyService';
import InterventionFileService from '../../../services/interventionFile/InterventionFileService';
import MentenancePlanningService from '../../../services/mentenancePlanning/MentenancePlanningService';
import RequestFileService from '../../../services/requestFile/RequestFileService';
import SystemTypeService from '../../../services/systemType/SystemTypeService';
import UserService from '../../../services/user/UserService';
import { RootState } from '../../../store';
import { canEditInterventionFile } from '../../../utils/usePermission';
import './InterventionFile.css';

const printerOperations = [
  'Demontare/montare carcasa',
  'Curatare la interior (aspirare/stergere)',
  'Verificarea functionarii angrenajelor',
  'Verificare stare cilindru/cuptor/developer',
  'Imprimare pagina de test',
];
const stationOperations = [
  'Demontare/montare capac/carcasa',
  'Curatare la interior/aspirare praf',
  'Curatare periferice (mouse, tastatura, monitor, UPS)',
  'Actualizare SO + antivirus, verificare aplicatii software',
];

const InterventionFilePage: FC<any> = () => {
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const handleResponse = useHandleResponse();
  const useQueryHook = () => new URLSearchParams(useLocation().search);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { profile } = useAuth();
  const query = useQueryHook();
  const queryFromRoute = new URLSearchParams(useLocation().search);

  const mentenancePlanningId = queryFromRoute.get('mentenancePlanningId');
  const companyId = queryFromRoute.get('companyId');
  const workPlaceId = queryFromRoute.get('workPlaceId');
  const assignedTechnicianId = query.get('assignedTechnicianId');
  const systemTypesParam = query.get('systemTypes');
  const systemTypesArray = systemTypesParam ? systemTypesParam.split(',') : [];

  const [id, setId] = useState<string>(query.get('id') || '');

  const [ip, setIp] = useState('');
  const [equipment, setEquipment] = useState('');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const [requestFile, setRequestFile] = useState<any>(null);
  const [requestFileId, setRequestFileId] = useState<any>(null);

  const [contactPersons, setContactPersons] = useState<any[]>();

  const [filterTextAgent, setFilterTextAgent] = useState('');
  const [filterTextCompany, setFilterTextCompany] = useState('');
  const [filterTextWorkPlace, setFilterTextWorkPlace] = useState('');
  const [filterTextTehnician, setFilterTextTehnician] = useState('');
  const [filterTextSystemType, setFilterTextSystemType] = useState('');

  const [showAdditionalFields, setShowAdditionalFields] = useState<boolean>(false);

  const reduxInterventionFile = useSelector((state: RootState) => state?.models?.interventionFile);
  const systemTypes = useSelector((state: RootState) => state?.store?.systemType.data);
  const companies = useSelector((state: RootState) => state?.store?.company.data);
  const users = useSelector((state: RootState) => state?.store?.user.data);

  const selectedCompany = useSelector((state: RootState) =>
    state.store?.company.data.find((c: any) => c?.id == state.models?.interventionFile?.companyId),
  );
  let workPlaces = selectedCompany?.workPlaces;

  const selectedWorkPlace = useSelector((state: RootState) =>
    selectedCompany?.workPlaces?.find(
      (workPlace: any) => workPlace?.id === state.models?.interventionFile?.workPlaceId,
    ),
  );

  const selectedContactPerson =
    reduxInterventionFile?.agent ??
    selectedWorkPlace?.contactPersons?.find((ct: any) => ct?.id == reduxInterventionFile?.agentId) ??
    companies
      ?.find((c: any) => c.id == reduxInterventionFile?.companyId)
      ?.contactPersons?.find((cp: any) => cp.id == reduxInterventionFile?.agentId);

  const initialValues = {
    number: useSelector((state: RootState) => state?.models?.interventionFile?.number),
    type: useSelector((state: RootState) => state?.models?.interventionFile?.type),

    companyId: useSelector((state: RootState) => state?.models?.interventionFile?.companyId),
    company: useSelector((state: RootState) => state?.models?.interventionFile?.company),
    workPlaceId: useSelector((state: RootState) => state?.models?.interventionFile?.workPlaceId),
    workPlace: useSelector((state: RootState) => state?.models?.interventionFile?.workPlace),
    agentId: useSelector((state: RootState) => state?.models?.interventionFile?.agentId),
    agent: useSelector((state: RootState) => state?.models?.interventionFile?.agent),

    requestFileId: useSelector((state: RootState) => state?.models?.interventionFile?.requestFileId),
    requestFile: useSelector((state: RootState) => state?.models?.interventionFile?.requestFile),

    interventionFileSystemTypes: useSelector(
      (state: RootState) => state?.models?.interventionFile?.interventionFileSystemTypes,
    ),
    hasJournal: useSelector((state: RootState) => state?.models?.interventionFile?.hasJournal),
    attachedImage: useSelector((state: RootState) => state?.models?.interventionFile?.attachedImage),

    reportedDefect: useSelector((state: RootState) => state?.models?.interventionFile?.reportedDefect),
    ascertainment: useSelector((state: RootState) => state?.models?.interventionFile?.ascertainment),

    deviceRepaired: useSelector((state: RootState) => state?.models?.interventionFile?.deviceRepaired),
    takenIntoService: useSelector((state: RootState) => state?.models?.interventionFile?.takenIntoService),
    estimatedPrice: useSelector((state: RootState) => state?.models?.interventionFile?.estimatedPrice),

    operations: useSelector((state: RootState) => state?.models?.interventionFile?.operations),

    isAccumulatorChanged: useSelector((state: RootState) => state?.models?.interventionFile?.isAccumulatorChanged),
    isBatteryChanged: useSelector((state: RootState) => state?.models?.interventionFile?.isBatteryChanged),
    consumptionDetails: useSelector((state: RootState) => state?.models?.interventionFile?.consumptionDetails),

    canBeUpgradedWithPresales: useSelector(
      (state: RootState) => state?.models?.interventionFile?.canBeUpgradedWithPresales,
    ),
    canBeUpgradedWithoutPresales: useSelector(
      (state: RootState) => state?.models?.interventionFile?.canBeUpgradedWithoutPresales,
    ),
    recommendationDetails: useSelector((state: RootState) => state?.models?.interventionFile?.recommendationDetails),

    helionEngineers: useSelector((state: RootState) => state?.models?.interventionFile?.helionEngineers),
    representativeSignature: useSelector(
      (state: RootState) => state?.models?.interventionFile?.representativeSignature,
    ),

    deliveryDate: useSelector((state: RootState) => state?.models?.interventionFile?.deliveryDate),
    timeTransport: useSelector((state: RootState) => state?.models?.interventionFile?.timeTransport),
    traveledKilometers: useSelector((state: RootState) => state?.models?.interventionFile?.traveledKilometers),
    laborTime: useSelector((state: RootState) => state?.models?.interventionFile?.laborTime),

    signature: useSelector((state: RootState) => state?.models?.interventionFile?.signature),

    contactPersonObservations: useSelector(
      (state: RootState) => state?.models?.interventionFile?.contactPersonObservations,
    ),

    latitude: useSelector((state: RootState) => state?.models?.interventionFile?.latitude),
    longitude: useSelector((state: RootState) => state?.models?.interventionFile?.longitude),

    isActive: useSelector((state: RootState) => state?.models?.interventionFile?.isActive),
    //remember: true,
  };

  const [isDraft, setIsDraft] = useState(false);
  const [hasEditPermission, setHasEditPermission] = useState<boolean>(true);

  // Check if user is technician (senior_tehnician or tehnician) but not admin
  const isTechnicianOnly =
    (profile?.userPermission?.rols?.includes('senior_tehnician') ||
      profile?.userPermission?.rols?.includes('tehnician') ||
      profile?.userPermission?.rols?.includes('dispatcher')) &&
    !profile?.userPermission?.rols?.includes('admin');

  // Check if this is a manual creation (no id, no mentenancePlanningId, no requestFileId from query)
  const requestFileIdFromQuery = query.get('requestFileId');
  const isManualCreation = !id && !mentenancePlanningId && !requestFileIdFromQuery;

  // Prevent technicians from creating intervention files manually
  useEffect(() => {
    if (isTechnicianOnly && isManualCreation) {
      message.error(
        'Nu aveți permisiunea de a crea fișe de intervenție manual. Puteți crea fișe doar din mentenanțe asignate.',
      );
      navigate('/service-maintenance/interventionFiles');
    }
  }, [isTechnicianOnly, isManualCreation, navigate, message]);

  // Early return if technician tries to create manually
  if (isTechnicianOnly && isManualCreation) {
    return (
      <div style={{ padding: '5%', textAlign: 'center' }}>
        <h2>Acces Restricționat</h2>
        <p>Nu aveți permisiunea de a crea fișe de intervenție manual. Puteți crea fișe doar din mentenanțe asignate.</p>
        <Button type='primary' onClick={() => navigate('/service-maintenance/interventionFiles')}>
          Înapoi la Lista Fișelor
        </Button>
      </div>
    );
  }

  useEffect(() => {
    setContactPersons([...(selectedWorkPlace?.contactPersons ?? []), ...(selectedCompany?.contactPersons ?? [])]);
  }, [selectedWorkPlace, selectedCompany]);

  useEffect(() => {
    if (companies?.length > 0 && companyId && workPlaceId) {
      const selectedCompany = companies.find((c: any) => c?.id == companyId);
      const selectedWorkPlace = selectedCompany?.workPlaces.find((w: any) => w?.id == workPlaceId);

      dispatch(updateField({ field: 'type', value: 'Mentenanta periodica' }));
      handleCompanyChange(selectedCompany);
      dispatch(updateField({ field: 'workPlace', value: selectedWorkPlace ?? null }));
      dispatch(updateField({ field: 'workPlaceId', value: selectedWorkPlace?.id ?? null }));
      const newSystemTypes = systemTypesArray
        .map((systemTypeName) => {
          const systemType = systemTypes.find((s: any) => s.name === systemTypeName);
          if (!systemType) return null; // Skip if not found

          return {
            id: '',
            interventionFileId: id,
            systemTypeId: systemType.id,
            systemType: { ...systemType },
            model: '',
            devicesNumber: 0,
            installBy: 'Helion',
          };
        })
        .filter((item) => item !== null); // Remove null items

      // Dispatch all new system types in a single batch
      const updatedInterventionFileSystemTypes = [
        ...reduxInterventionFile?.interventionFileSystemTypes,
        ...newSystemTypes,
      ];

      dispatch(
        updateField({
          field: 'interventionFileSystemTypes',
          value: updatedInterventionFileSystemTypes,
        }),
      );

      // Set the form values as well
      form.setFieldsValue({
        interventionFileSystemTypes: updatedInterventionFileSystemTypes,
      });

      const defaultContactPerson = contactPersons?.find((c: any) => c?.isDefaultOnInterventionFile === true);

      if (defaultContactPerson) {
        handleAgentChange(defaultContactPerson);
      }

      var tehnician = users.find((u: any) => u?.id == assignedTechnicianId);
      if (tehnician) {
        onChangeEngineer(tehnician, true);
      }
    }

    if (mentenancePlanningId) {
      // If you also pass an ID for fetching intervention file data
      //dispatch(fetchInterventionFileData(id));
    }
  }, [companies, companyId, workPlaceId, id, systemTypes, dispatch]);

  useEffect(() => {
    form.setFieldsValue({
      ...form?.getFieldsValue(true),
      signature: profile?.signature,
    });
    dispatch(updateField({ field: 'signature', value: profile?.signature }));
  }, [profile]);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          form.setFieldsValue({
            ...form.getFieldsValue(true),
            latitude: latitude,
            longitude: longitude,
          });
        },
        (error) => {
          if (error.code === error.PERMISSION_DENIED) {
            message.error('Nu puteti genera fise fara a permite locatia. Dati refresh pentru a reincepe actiunea');
          } else {
            message.error('Error getting location: ' + error.message);
          }
        },
      );
    } else {
      message.error('Geolocation is not supported by this browser.');
    }
  }, [id]);

  useEffect(() => {
    const idFromUrl = query.get('id');
    const requestFileIdFromUrl = query.get('requestFileId');

    if (!idFromUrl) {
      form.resetFields();
    }

    if (requestFileIdFromUrl) {
      fetchRequestFile(requestFileIdFromUrl);
    }

    setId(idFromUrl || '');
    dispatch(updateField({ field: 'requestFileId', value: requestFileIdFromUrl || null }));
  }, [location.search]);

  useEffect(() => {
    if (reduxInterventionFile?.requestFileId && companies?.length > 0 && systemTypes?.length > 0) {
      const company = companies?.find((c: any) => c?.id === reduxInterventionFile?.requestFile?.companyId);
      handleCompanyChange(company!);

      const workPlace = company?.workPlaces?.find(
        (c: any) => c?.id === reduxInterventionFile?.requestFile?.workPlaceId,
      );
      dispatch(updateField({ field: 'workPlace', value: workPlace ?? null }));
      dispatch(updateField({ field: 'workPlaceId', value: workPlace?.id ?? null }));
      dispatch(updateField({ field: 'reportedDefect', value: reduxInterventionFile?.requestFile?.remarks ?? '' }));

      form.setFieldsValue({
        ...form.getFieldsValue(true),
        workPlace: workPlace,
        workPlaceId: workPlace?.id,
        agentIds: workPlace?.agents?.map((a: any) => a?.id),
      });

      const agent =
        workPlace?.contactPersons?.find((c: any) => c?.isDefaultOnInterventionFile === true) ??
        reduxInterventionFile?.requestFile?.agent;

      handleAgentChange(agent);

      if (reduxInterventionFile?.requestFile?.assignedTechnician) {
        onChangeEngineer(reduxInterventionFile?.requestFile?.assignedTechnician, true);
      }

      dispatch(
        updateField({
          field: 'interventionFileSystemTypes',
          value: reduxInterventionFile?.requestFile?.requestFileSystemTypes,
        }),
      );

      dispatch(
        updateField({
          field: 'laborTime',
          value: Math.round(
            (Number(new Date(reduxInterventionFile?.requestFile?.interventionEnd)) -
              Number(new Date(reduxInterventionFile?.requestFile?.interventionStart))) /
              60000,
          ),
        }),
      );
      dispatch(updateField({ field: 'type', value: 'Service [cu contract]' }));

      setShowAdditionalFields(false);

      form.setFieldsValue({
        ...form.getFieldsValue(true),
        type: 'Service [cu contract]',
        agent: agent,
        agentId: agent?.id,
        interventionFileSystemTypes: reduxInterventionFile?.requestFile?.requestFileSystemTypes,
        timeTransport: Math.round(reduxInterventionFile?.requestFile?.elapsedTime ?? 1),
        traveledKilometers: Math.round(reduxInterventionFile?.requestFile?.traveledKm ?? 1),
        laborTime:
          Math.round(
            (Number(new Date(reduxInterventionFile?.requestFile?.interventionEnd)) -
              Number(new Date(reduxInterventionFile?.requestFile?.interventionStart))) /
              60000,
          ) ?? 1,
      });
    }
    if (id && companies?.length > 0 && systemTypes?.length > 0) {
      InterventionFileService.search({
        ids: [id],
        isInterventionFileSystemTypeIncluded: true,
        isInterventionFileEngineerIncluded: true,
        isInterventionFilePerformOperationsIncluded: true,
        isInterventionFileEquipmentsIncluded: true,
        isMentenancePlanningIncluded: true,
        isCompanyIncluded: true,
        isWorkPlaceIncluded: true,
        isAgentIncluded: true,
        isRequestFileIncluded: true,
      }).then((data) => {
        const interventionFile = data?.response[0];
        dispatch(setInterventionFile(interventionFile));
        form.setFieldsValue({
          ...interventionFile,
        });

        // Check edit permissions
        if (interventionFile) {
          const canEdit = canEditInterventionFile(interventionFile, profile);
          setHasEditPermission(canEdit);

          if (!canEdit) {
            message.error('Nu aveți permisiunea de a modifica această fișă de intervenție.');
            navigate('/service-maintenance/interventionFiles');
          }
        }

        return interventionFile;
      });
    }
  }, [id, companies, systemTypes, users]);

  const { data: _companies, isFetching: isCompaniesLoading } = useQuery({
    queryKey: ['companies'],
    queryFn: () =>
      CompanyService.search({
        isAgentIncluded: true,
        isWorkPlacesIncluded: true,
        isAccountManagerIncluded: true,
        isActive: true,
      }).then((data) => {
        dispatch(setCompanies(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const {
    data: _company,
    isFetching: isCompanyLoading,
    refetch: searchCompany,
  } = useQuery({
    queryKey: ['companies'],
    queryFn: () =>
      CompanyService.search({
        ids: [reduxInterventionFile?.companyId],
        isAgentIncluded: true,
        isWorkPlacesIncluded: true,
        isAccountManagerIncluded: true,
        isActive: true,
      }).then((data) => {
        if (data?.response) {
          form.setFieldsValue({
            ...form.getFieldsValue(true),
            companyId: data?.response[0]?.id,
            company: data?.response[0],
            workPlaceIds: data?.response[0]?.workPlaces?.map((c: any) => c?.id),
          });

          dispatch(updateField({ field: 'company', value: data?.response[0] ?? null }));
          dispatch(updateField({ field: 'companyId', value: data?.response[0]?.id ?? null }));
        }
        return data?.response;
      }),
    enabled: !!reduxInterventionFile?.companyId,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: _systemTypes, isFetching: isSystemTypesLoading } = useQuery({
    queryKey: ['systemTypes'],
    queryFn: () =>
      SystemTypeService.search({
        isActive: true,
      }).then((data) => {
        dispatch(setSystemTypes(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: _users, isFetching: isUsersLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () =>
      UserService.search({
        rols: 'tehnician , senior_tehnician',
        isPersonalDataIncluded: true,
        isActive: true,
      }).then((data) => {
        dispatch(setUsers(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: _status, isFetching: isRequestStatusLoading } = useQuery({
    queryKey: ['requestStatus'],
    queryFn: () =>
      RequestFileService.getStatuses().then((data) => {
        dispatch(setRequestFileStatuses(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: _dates, isFetching: isMentenancePlanningLoading } = useQuery({
    queryKey: ['mentenancePlannings'],
    queryFn: () =>
      MentenancePlanningService.searchMyActiveMentenancePlanning({
        ids: [mentenancePlanningId!],
        assignedTechnicianId: profile?.id,
        isMyActiveMentenancePlannings: true,
        isStatusIncluded: true,
        isCompanyIncluded: true,
        isWorkPlaceIncluded: true,
        isAssignedTechnicianIncluded: true,
        isMentenancePlanningSystemTypeIncluded: true,
      }).then((data) => {
        return data?.response;
      }),
    enabled: !!profile?.id && !!mentenancePlanningId,
    refetchOnWindowFocus: false,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  const { data: _draftStatus } = useQuery({
    queryKey: ['interventionFile', 'draft', id],
    queryFn: () =>
      InterventionFileService.checkDraftStatus(id).then((data) => {
        setIsDraft(data?.isDraft ?? false);
      }),
    enabled: !!id,
  });

  const handleReset = () => {
    form.resetFields();
    form.setFieldsValue(initialState);
  };

  const handleCompanyChange = (item: BaseCompany) => {
    const company = companies.find((date: any) => date?.name === item?.name);

    dispatch(updateField({ field: 'company', value: company ?? null }));
    dispatch(updateField({ field: 'companyId', value: company?.id ?? null }));
    setTimeout(() => {
      if (company) {
        searchCompany();
      }
    }, 500);
  };

  const handleWorkPlaceChange = (item: WorkPlace) => {
    const workPlace = workPlaces.find((date: any) => date.name === item.name);

    form.setFieldsValue({
      ...form.getFieldsValue(true),
      workPlace: workPlace,
      workPlaceId: workPlace?.id,
      agentIds: workPlace?.agents?.map((a: any) => a?.id),
    });

    dispatch(updateField({ field: 'workPlace', value: workPlace ?? null }));
    dispatch(updateField({ field: 'workPlaceId', value: workPlace?.id ?? null }));

    const defaultContactPerson = contactPersons?.find((c: any) => c?.isDefaultOnInterventionFile === true);

    if (defaultContactPerson) {
      handleAgentChange(defaultContactPerson);
    }
  };

  const handleAgentChange = (agent: any) => {
    setShowAdditionalFields(false);

    // Update contactPersons to include the new agent if not already in the list
    const updatedContactPersons = [...contactPersons!, agent];

    // Now, find the newly added agent using the agent's ID, not name
    const findedAgent = updatedContactPersons.find((a: any) => a?.id === agent?.id);

    // Update the form fields
    form.setFieldsValue({
      ...form.getFieldsValue(true),
      agent: agent,
      agentId: findedAgent?.id,
    });

    // Dispatch to redux to update agent in the store
    dispatch(updateField({ field: 'agent', value: agent ?? null }));
    dispatch(updateField({ field: 'agentId', value: agent?.id ?? null }));

    // Optionally, update the contactPersons state if needed for the UI
    //setContactPersons(updatedContactPersons);
  };

  const handleAddNewPersonChange = (e: any) => {
    if (e.target.checked) {
      const agent = {
        id: '',
        name: form.getFieldValue('agentName'),
        phone: form.getFieldValue('agentPhone'),
        email: form.getFieldValue('agentEmail'),
        role: form.getFieldValue('agentRole'),
      };

      form.setFieldsValue({
        ...form.getFieldsValue(true),
        agent: agent,
        agentId: '',
      });

      dispatch(updateField({ field: 'agent', value: agent ?? null }));
      dispatch(updateField({ field: 'agentId', value: agent?.id ?? null }));
      setShowAdditionalFields(true);
    } else {
      setShowAdditionalFields(false);
    }
  };

  const uploadImage = async (file: any) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await InterventionFileService.upload(formData);
      const data = await response.json();
      form.setFieldsValue({
        ...form.getFieldsValue(true),
        attachedImage: data?.response,
      });
      dispatch(updateField({ field: 'attachedImage', value: data?.response ?? null }));

      if (data?.errors?.length > 0) {
        message.error(data?.errors[0]);
      } else {
        message.success('Imagine incarcata!');
      }
    } catch (error: any) {
      message.error(error.message);
    }
  };

  const handleDeleteImage = () => {
    // Reset the image in the form
    form.setFieldsValue({
      ...form.getFieldsValue(true),
      attachedImage: '',
    });

    // Update the Redux state to remove the image
    dispatch(updateField({ field: 'attachedImage', value: '' }));

    message.success('Imagine stearsa!');
  };

  const beforeUpload = (file: any) => {
    const isImage = file.type === 'image/jpeg' || file.type === 'image/png';
    if (!isImage) {
      message.error('Poti adauga doar imagini (JPEG sau PNG)!');
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  const getUploadProps = () => ({
    listType: 'picture' as 'picture',
    maxCount: 1,
    beforeUpload: (file: any) => beforeUpload(file),
    customRequest: ({ file, onSuccess, onError }: any) => {
      uploadImage(file)
        .then(() => {
          onSuccess('ok');
        })
        .catch((error) => {
          onError(error);
        });
    },
  });

  const handleClientSignatureChange = (signatureData: string) => {
    form.setFieldsValue({
      ...form.getFieldsValue(true),
      signature: signatureData,
    });
    dispatch(updateField({ field: 'signature', value: signatureData ?? null }));
  };

  const handleRepresentativeSignatureChange = (signatureData: string) => {
    form.setFieldsValue({
      ...form.getFieldsValue(true),
      representativeSignature: signatureData,
    });
    dispatch(updateField({ field: 'representativeSignature', value: signatureData ?? null }));
  };

  const onFinish = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;

          // Check if this is a draft save
          const isDraftSave = !form.getFieldValue('representativeSignature');

          if (isDraftSave) {
            // Save as draft without full validation
            await onSaveInterventionFile(latitude, longitude, true);
            message.success('Draft saved successfully');
            return;
          }

          // Proceed with full validation and save
          await onSaveInterventionFile(latitude, longitude, false);
        },
        (error) => {
          if (error.code === error.PERMISSION_DENIED) {
            message.error('You cannot generate files without allowing location access. Refresh to retry.');
          } else {
            message.error('Error getting location: ' + error.message);
          }
        },
      );
    } else {
      message.error('Geolocation is not supported by this browser.');
    }
  };

  const onSaveInterventionFile = async (latitude: any, longitude: any, isDraft: boolean) => {
    const requestFileSystemTypes = reduxInterventionFile?.requestFile?.requestFileSystemTypes || [];
    const interventionFileSystemTypes = reduxInterventionFile?.interventionFileSystemTypes || [];
    const mentenancePlanningSystemTypes = _dates?.[0]?.mentenancePlanningSystemTypes || [];

    // Function to count occurrences of systemTypeId in an array
    const countSystemTypes = (arr: any[]) => {
      return arr.reduce(
        (acc, item) => {
          acc[item.systemTypeId] = (acc[item.systemTypeId] || 0) + 1;
          return acc;
        },
        {} as Record<string, number>,
      );
    };

    // Validate if interventionFileSystemTypes exists and has records
    if (!Array.isArray(interventionFileSystemTypes) || interventionFileSystemTypes.length === 0) {
      message.error('Sistemele de intervenție sunt goale.');
      return;
    }

    // Validate requestFileSystemTypes if it exists and compare with interventionFileSystemTypes
    if (Array.isArray(requestFileSystemTypes) && requestFileSystemTypes.length > 0) {
      const requestCounts = countSystemTypes(requestFileSystemTypes);
      const interventionCounts = countSystemTypes(interventionFileSystemTypes);

      const isMatchingRequest =
        Object.keys(requestCounts).every((id) => requestCounts[id] === interventionCounts[id]) &&
        Object.keys(interventionCounts).every((id) => requestCounts[id] === interventionCounts[id]);

      if (!isMatchingRequest) {
        message.error('Sistemele selectate nu coincid cu cele de pe solicitare.');
        return;
      }
    }

    // Validate mentenancePlanningSystemTypes if it exists and compare with interventionFileSystemTypes
    if (Array.isArray(mentenancePlanningSystemTypes) && mentenancePlanningSystemTypes.length > 0) {
      const maintenanceCounts = countSystemTypes(mentenancePlanningSystemTypes);
      const interventionCounts = countSystemTypes(interventionFileSystemTypes);

      const isMatchingMaintenance =
        Object.keys(maintenanceCounts).every((id) => maintenanceCounts[id] === interventionCounts[id]) &&
        Object.keys(interventionCounts).every((id) => maintenanceCounts[id] === interventionCounts[id]);

      if (!isMatchingMaintenance) {
        message.error('Sistemele selectate nu coincid cu cele de pe mentenanta.');
        return;
      }
    }

    saveInterventionFile({
      ...reduxInterventionFile,
      id: id,
      latitude: latitude,
      longitude: longitude,
      mentenancePlanningId: mentenancePlanningId,
      isDraft: isDraft,
    });
  };

  const handleSystemTypeChange = (systemType: any, isChecked: boolean) => {
    let filtredInterventionFileSystemTypes = [];
    if (isChecked) {
      filtredInterventionFileSystemTypes = [
        ...reduxInterventionFile?.interventionFileSystemTypes,
        {
          id: '',
          interventionFileId: id,
          systemTypeId: systemType?.id,
          systemType: { ...systemType },
          model: '',
          devicesNumber: 0,
          installBy: 'Helion',
        },
      ];

      form.setFieldsValue({
        interventionFileSystemTypes: filtredInterventionFileSystemTypes,
      });
    } else {
      filtredInterventionFileSystemTypes = reduxInterventionFile?.interventionFileSystemTypes?.filter(
        (st: any) => st?.systemTypeId !== systemType?.id,
      );
      form.setFieldsValue({
        interventionFileSystemTypes: filtredInterventionFileSystemTypes,
      });
    }
    dispatch(
      updateField({
        field: 'interventionFileSystemTypes',
        value: filtredInterventionFileSystemTypes,
      }),
    );
  };

  const onModelChange = (systemType: InterventionFileSystemType | null | undefined, value: string) => {
    const updatedSystemTypes = reduxInterventionFile?.interventionFileSystemTypes.map((ifst: any) => {
      if (ifst.systemTypeId === systemType?.systemTypeId) {
        return { ...ifst, model: value };
      }
      return ifst;
    });
    dispatch(
      updateField({
        field: 'interventionFileSystemTypes',
        value: updatedSystemTypes,
      }),
    );
  };

  const onDeviceNumberChange = (systemType: InterventionFileSystemType | null | undefined, value: any) => {
    const updatedSystemTypes = reduxInterventionFile?.interventionFileSystemTypes.map((ifst: any) => {
      if (ifst.systemTypeId === systemType?.systemTypeId) {
        return { ...ifst, devicesNumber: value };
      }
      return ifst;
    });
    dispatch(
      updateField({
        field: 'interventionFileSystemTypes',
        value: updatedSystemTypes,
      }),
    );
  };

  const onChangeIntallBy = (systemType: InterventionFileSystemType | null | undefined, value: string) => {
    const updatedSystemTypes = reduxInterventionFile?.interventionFileSystemTypes.map((ifst: any) => {
      if (ifst.systemTypeId === systemType?.systemTypeId) {
        return { ...ifst, installBy: value };
      }
      return ifst;
    });
    dispatch(
      updateField({
        field: 'interventionFileSystemTypes',
        value: updatedSystemTypes,
      }),
    );
  };

  const onChangeEngineer = (user: any, isChecked: boolean) => {
    if (isChecked) {
      const filterdEnginieers = [
        ...reduxInterventionFile?.helionEngineers,
        { id: '', userId: user?.id, interventionFileId: id, user: { ...user } },
      ];
      form.setFieldsValue({
        helionEngineers: filterdEnginieers,
      });
      dispatch(updateField({ field: 'helionEngineers', value: filterdEnginieers ?? null }));
    } else {
      const filterdEnginieers = reduxInterventionFile?.helionEngineers.filter(
        (engineer: any) => engineer.userId !== user?.id,
      );

      form.setFieldsValue({
        helionEngineers: filterdEnginieers,
      });
      dispatch(updateField({ field: 'helionEngineers', value: filterdEnginieers ?? null }));
    }
  };

  const handleSubmitAdditionalPerson = () => {
    form
      .validateFields(['agentName', 'agentPhone', 'agentEmail', 'agentRole'])
      .then(async (values) => {
        await AgentService.upsert('', {
          name: values.agentName,
          phone: values.agentPhone,
          email: values.agentEmail,
          isActive: true,
          isDefaultOnInterventionFile: values.isDefaultOnInterventionFile ?? false,
          role: values.agentRole,
          workPlaceId: form.getFieldValue('workPlaceId'),
          companyId: form.getFieldValue('companyId'),
        })
          .then(async (data) => {
            if (data) {
              message.success('Agentul a fost adaugat cu succes');

              await AgentService.search({
                workPlaceId: form.getFieldValue('workPlaceId'),
              }).then((agentsData) => {
                setContactPersons([...agentsData.response, ...selectedCompany?.contactPersons]);

                handleAgentChange(data?.response);

                setShowAdditionalFields(false);
              });
            } else {
              console.error('Received data is not in JSON format or JSON parsing failed.');
            }
          })
          .catch((error) => {
            message.error(`Error processing your request: ${error}`);
          });
      })
      .catch((errorInfo) => {
        message.error(`Validation failed: ${errorInfo}`);
      });
  };

  const handleFieldChange = (changedValues: any, _allValues: any) => {
    for (const key in changedValues) {
      dispatch(updateField({ field: key, value: changedValues[key] }));
    }
  };

  const handleCheckBoxChange = (inputName: any, value: any) => {
    form.setFieldsValue({
      [inputName]: value,
    });
    dispatch(updateField({ field: inputName, value: value }));
  };

  const handleAddEquipment = () => {
    const newEquipment = {
      id: '',
      interventionFile: reduxInterventionFile.id,
      equipment,
      ip,
    };

    dispatch(addInterventionFileEquipments(newEquipment));

    // Reset form
    setEquipment('');
    setIp('');
    setEditingIndex(null);
  };

  // Handle selecting an item for editing
  const handleEditEquipment = (index: number) => {
    const item = reduxInterventionFile.interventionFileEquipments[index];
    setEquipment(item.equipment);
    setIp(item.ip);
    setEditingIndex(index);
  };

  // Handle updating an existing item
  const handleUpdateEquipment = () => {
    if (editingIndex === null) return;

    const updatedEquipment = {
      ...reduxInterventionFile.interventionFileEquipments[editingIndex],
      equipment,
      ip,
    };

    dispatch(updateInterventionFileEquipments({ index: editingIndex, updatedEquipment }));

    // Reset form
    setEquipment('');
    setIp('');
    setEditingIndex(null);
  };

  // Handle deleting an equipment item
  const handleDeleteEquipment = (index: number) => {
    dispatch(deleteInterventionFileEquipmentsByIndex(index));

    // If deleting the item being edited, reset the form
    if (editingIndex === index) {
      setEquipment('');
      setIp('');
      setEditingIndex(null);
    }
  };

  const handleDeviceChange = (value: any) => {
    dispatch(updateField({ field: 'device', value: value }));
    dispatch(updateField({ field: 'interventionFilePerformOperations', value: [] }));
  };

  const { mutate: saveInterventionFile, isPending: isSavingInterventionFile } = useMutation({
    mutationFn: (formData: any) => InterventionFileService.upsert(id, formData),
    onSuccess: async (data: any) => {
      if (!data || typeof data !== 'object') {
        console.error('Datele primite nu sunt valide.');
        message.error('A apărut o eroare la salvarea fișei. Încearcă din nou.');
        return;
      }

      if (Array.isArray(data.errors) && data.errors.length > 0) {
        data.errors.forEach((item: any) => {
          message.error(item);
        });
        return; // 👉 Oprește execuția dacă există erori
      }

      const fileId = data?.response?.id;
      const fileNumber = data?.response?.number;

      if (requestFileId) {
        try {
          await deleteNotificationFromTelegram(requestFile?.telegramMessageId);
        } catch (err) {
          console.error('Eroare la ștergerea notificării din Telegram:', err);
          message.warning('Notificarea Telegram nu a putut fi ștearsă.');
        }
      }

      if (!!mentenancePlanningId && fileId) {
        let mentenancePlanningRecord = _dates[0];
        let mentenancePlanning = {
          ...mentenancePlanningRecord,
          interventionFileId: fileId,
          statusId: 4,
          history: [
            ...mentenancePlanningRecord?.history,
            `[Finalizata] - ${dayjs().format('DD-MM-YYYY HH:mm')}] - FISA: ${fileNumber}`,
          ],
          lastMentenance: dayjs().format('DD-MM-YYYY HH:mm'),
        };
        await saveMentenancePlanning(mentenancePlanning);
      }

      message.success(
        <span>
          <span className='font-weight-bold'>Fisa {fileNumber}</span> a fost {!!id ? 'modificata' : 'adaugata'} cu
          success
        </span>,
      );

      navigate(`/service-maintenance/interventionFile/edit?id=${fileId}`);
    },
    onError: (error: any) => {
      console.error('Eroare la salvarea fișei:', error);
      message.error('Salvarea fișei a eșuat. Încearcă din nou.');
    },
  });

  const { mutate: saveMentenancePlanning, isPending: isSavingMentenancePlanning } = useMutation({
    mutationFn: (formData: any) => MentenancePlanningService.upsert(formData?.id, formData),
    onSuccess: (data: any) => {
      if (data) {
        navigate(`/service-maintenance/interventionFiles`);
        handleResponse(data, `Mentenanta ${data?.response?.number} a fost finalizata cu succes.`);
      } else {
        console.error('Received data is not in JSON format or JSON parsing failed.');
      }
    },
  });

  const { mutate: deleteNotificationFromTelegram, isPending: isDeletingNotificationFromTelegram } = useMutation({
    mutationFn: (telegramMessageId: any) => RequestFileService.deleteNotification(telegramMessageId),
    onSuccess: (data: any) => {
      if (data && data.message) {
        navigate(`/service-maintenance/dispatch/dispatch`);
      } else {
        console.error('Received data is not in JSON format or JSON parsing failed.');
      }
    },
  });

  const fetchRequestFile = async (requestFileIdFromUrl: string) => {
    try {
      const data = await RequestFileService.search({
        ids: [requestFileIdFromUrl],
        isContractIncluded: true,
        isWorkPlaceIncluded: true,
        isCompanyIncluded: true,
        isAgentIncluded: true,
        isStatusIncluded: true,
        isRequestFileSystemTypesIncluded: true,
        isAssignedTechnicianIncluded: true,
      });

      if (data?.response?.length) {
        const file = data.response[0];
        setRequestFile(file);
        setRequestFileId(file.id);
        dispatch(updateField({ field: 'requestFile', value: file }));
        dispatch(updateField({ field: 'requestFileId', value: file.id }));

        dispatch(updateField({ field: 'laborTime', value: Math.round(file?.laborTime ?? 1) }));
        dispatch(updateField({ field: 'timeTransport', value: Math.round(file?.elapsedTime ?? 1) }));
        dispatch(updateField({ field: 'traveledKilometers', value: Math.round(file?.traveledKm ?? 1) }));
      } else {
        console.error('No request file found.');
      }
    } catch (error) {
      console.error('Error fetching request file:', error);
    }
  };

  if (
    isCompaniesLoading ||
    isUsersLoading ||
    isRequestStatusLoading ||
    isSystemTypesLoading ||
    isSavingInterventionFile ||
    isCompanyLoading ||
    isMentenancePlanningLoading ||
    isSavingMentenancePlanning ||
    isDeletingNotificationFromTelegram
  ) {
    return <Spin size='large' className='spinner'></Spin>;
  }

  // If user doesn't have edit permission, show read-only view
  if (!hasEditPermission) {
    return (
      <div style={{ padding: '5%', textAlign: 'center' }}>
        <h2>Acces Restricționat</h2>
        <p>Nu aveți permisiunea de a modifica această fișă de intervenție.</p>
        <Button type='primary' onClick={() => navigate('/service-maintenance/interventionFiles')}>
          Înapoi la Lista Fișelor
        </Button>
      </div>
    );
  }

  return (
    <>
      <div style={{ padding: '5%' }}>
        <Form
          layout='vertical'
          form={form}
          onValuesChange={handleFieldChange}
          onFinish={onFinish}
          initialValues={initialValues}
        >
          <div style={{ backgroundColor: 'lightgray' }}>
            <Title level={2}>FISA HELION SECURITY</Title>
            <Title level={3}>Proces verbal de predate-primire</Title>
            <Title level={4}>Numar fisa/P.V: {reduxInterventionFile?.number}</Title>
          </div>
          <div>
            <Form.Item
              label='Tip manopera'
              name='type'
              rules={[{ required: true, message: 'Alegeti tipul de manopera!' }]}
            >
              <Select onSelect={(e) => dispatch(updateField({ field: 'type', value: e }))}>
                <Select.Option value='Service [cu contract]'>Service [cu contract]</Select.Option>
                <Select.Option value='Service [fara contract]'>Service [fara contract]</Select.Option>
                <Select.Option value='Garantie'>Garantie</Select.Option>
                <Select.Option value='Mentenanta periodica'>Mentenanta periodica</Select.Option>
              </Select>
            </Form.Item>
          </div>
          <hr />
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-evenly' }}>
            <Form.Item name='company' rules={[{ required: true, message: 'Alegeti o firma!' }]}>
              <>
                <h3>Detalii firma</h3>
                <div style={{ width: '300px', margin: '0 auto' }}>
                  <Input
                    placeholder='Cauta firma'
                    value={filterTextCompany}
                    onChange={(e) => setFilterTextCompany(e.target.value)}
                    style={{ marginBottom: '10px' }}
                  />
                  <div
                    style={{
                      height: '300px',
                      overflowY: 'auto',
                      border: '1px solid #d9d9d9',
                      borderRadius: '4px',
                    }}
                  >
                    <List
                      bordered
                      dataSource={companies.filter((item: any) =>
                        item.name.toLowerCase().includes(filterTextCompany.toLowerCase()),
                      )}
                      renderItem={(company: any) => (
                        <List.Item>
                          <Checkbox
                            checked={selectedCompany?.name == company.name}
                            onChange={() => handleCompanyChange(company)}
                          >
                            {
                              <>
                                <div>
                                  <strong>{company.name}</strong>
                                </div>
                                <div>CUI: {company.cui}</div>
                              </>
                            }
                          </Checkbox>
                        </List.Item>
                      )}
                    />
                  </div>
                </div>
              </>
            </Form.Item>
            <Form.Item name='workPlace' rules={[{ required: true, message: 'Alegeti un punct de lucru!' }]}>
              <>
                <h3>Punct de lucru</h3>
                <div style={{ width: '300px', margin: '0 auto' }}>
                  <Input
                    placeholder='Cauta punct de lucru'
                    value={filterTextWorkPlace}
                    onChange={(e) => setFilterTextWorkPlace(e.target.value)}
                    style={{ marginBottom: '10px' }}
                  />
                  <div
                    style={{
                      height: '300px',
                      overflowY: 'auto',
                      border: '1px solid #d9d9d9',
                      borderRadius: '4px',
                    }}
                  >
                    <List
                      bordered
                      dataSource={workPlaces?.filter((item: any) =>
                        item?.name?.toLowerCase()?.includes(filterTextWorkPlace.toLowerCase()),
                      )}
                      renderItem={(workPlace: any) => (
                        <List.Item>
                          <Checkbox
                            checked={selectedWorkPlace?.name == workPlace.name}
                            onChange={() => handleWorkPlaceChange(workPlace)}
                          >
                            <>
                              <div>
                                <strong>{workPlace.name}</strong>
                              </div>
                              <div>Oras: {workPlace.town}</div>
                              <div>Adresa: {workPlace.address}</div>
                            </>
                          </Checkbox>
                        </List.Item>
                      )}
                    />
                  </div>
                </div>
              </>
            </Form.Item>
            <Form.Item name='agent' rules={[{ required: true, message: 'Alegeți un reprezentant!' }]}>
              <>
                <h3>Reprezentant Client</h3>
                <div style={{ width: '300px', margin: '0 auto' }}>
                  <Input
                    placeholder='Caută reprezentant'
                    value={filterTextAgent}
                    onChange={(e) => setFilterTextAgent(e.target.value)}
                    style={{ marginBottom: '10px' }}
                  />

                  <div
                    style={{
                      height: '300px',
                      overflowY: 'auto',
                      border: '1px solid #d9d9d9',
                      borderRadius: '4px',
                    }}
                  >
                    <List
                      bordered
                      dataSource={[
                        ...Array.from(
                          new Map(
                            (contactPersons ?? [])
                              .filter((agent: any) =>
                                agent?.name?.toLowerCase()?.includes(filterTextAgent?.toLowerCase()),
                              )
                              .map((agent) => [agent.name, agent]), // Use name as unique key
                          ).values(),
                        ),
                        { isAddNew: true },
                      ]}
                      renderItem={(agent) => (
                        <List.Item>
                          {selectedWorkPlace && agent?.isAddNew ? (
                            <Checkbox checked={showAdditionalFields} onChange={handleAddNewPersonChange}>
                              Adaugă o altă persoană
                            </Checkbox>
                          ) : selectedWorkPlace ? (
                            <Checkbox
                              checked={selectedContactPerson?.name === agent?.name}
                              onChange={() => handleAgentChange(agent)}
                            >
                              <>
                                <div>
                                  <strong>{agent?.name}</strong>
                                </div>
                                <div>Telefon: {agent?.phone}</div>
                                <div>Email: {agent?.email}</div>
                                <div>Funcție: {agent?.role?.join('/')}</div>
                              </>
                            </Checkbox>
                          ) : (
                            <></>
                          )}
                        </List.Item>
                      )}
                    />
                  </div>
                </div>
              </>
            </Form.Item>

            {showAdditionalFields && (
              <div style={{ marginTop: 16 }}>
                <Form.Item name='agentName' rules={[{ required: true, message: 'Introduceți numele!' }]}>
                  <Input placeholder='Nume' />
                </Form.Item>
                <Form.Item name='agentPhone' rules={[{ required: true, message: 'Introduceți numărul de telefon!' }]}>
                  <Input placeholder='Număr de telefon' />
                </Form.Item>
                <Form.Item name='agentEmail' rules={[{ required: true, message: 'Introduceți emailul!' }]}>
                  <Input placeholder='Email' />
                </Form.Item>
                <Form.Item name='agentRole' rules={[{ required: true, message: 'Introduceți rolul!' }]}>
                  <Input placeholder='Rol' />
                </Form.Item>
                <Button type='primary' onClick={handleSubmitAdditionalPerson}>
                  Adaugă
                </Button>
              </div>
            )}
          </div>
          <hr />
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-evenly' }}>
            <Form.Item name='interventionFileSystemTypes' rules={[{ required: true, message: 'Alegeri un sistem!' }]}>
              <>
                <h3>Tip Sistem</h3>
                <div style={{ width: '300px', margin: '0 auto' }}>
                  <Input
                    placeholder='Cauta tip sistem'
                    value={filterTextSystemType}
                    onChange={(e) => setFilterTextSystemType(e.target.value)}
                    style={{ marginBottom: '10px' }}
                  />
                  <div
                    style={{
                      height: '300px',
                      overflowY: 'auto',
                      border: '1px solid #d9d9d9',
                      borderRadius: '4px',
                    }}
                  >
                    <List
                      dataSource={systemTypes.filter((item: any) =>
                        item.name.toLowerCase().includes(filterTextSystemType.toLowerCase()),
                      )}
                      renderItem={(item: any) => (
                        <List.Item style={{ marginLeft: '10%' }}>
                          <Checkbox
                            checked={reduxInterventionFile?.interventionFileSystemTypes?.some(
                              (system: any) => system?.systemTypeId === item?.id,
                            )}
                            onChange={(e) => handleSystemTypeChange(item, e.target.checked)}
                          >
                            {item.name}
                          </Checkbox>
                        </List.Item>
                      )}
                    />
                  </div>
                  {reduxInterventionFile?.interventionFileSystemTypes?.map((systemType: any, index: any) => (
                    <div key={index}>
                      <Form.Item rules={[{ required: true, message: 'Introduceti modelul!' }]}>
                        <Input
                          addonBefore={'Model ' + systemType?.systemType?.name}
                          placeholder='HIKVISON'
                          value={systemType?.model}
                          onChange={(e) => onModelChange(systemType, e.target.value)}
                        />
                      </Form.Item>
                      <Form.Item
                        rules={[
                          {
                            required: true,
                            message: 'Introduceti numarul de dispozitive!',
                          },
                        ]}
                      >
                        <InputNumber
                          addonBefore='Numar componente'
                          min={0}
                          value={systemType?.devicesNumber ?? 0}
                          onChange={(value) => onDeviceNumberChange(systemType, value)}
                        />
                      </Form.Item>
                      <Form.Item>
                        <Input
                          addonBefore='Instalat de'
                          value={systemType?.installBy ?? 'Helion'}
                          onChange={(e) => onChangeIntallBy(systemType, e.target.value)}
                        />
                      </Form.Item>
                      <hr />
                    </div>
                  ))}
                </div>
              </>
            </Form.Item>
            {/* Wrap the additional I.T. general fields inside a Row to display them next to the interventionFileSystemTypes */}
            <div style={{ width: '300px' }}>
              <Row gutter={16} style={{ width: '100%' }}>
                <Col xs={24}>
                  {/* Conditionally render additional fields for "I.T. general" */}
                  {reduxInterventionFile?.interventionFileSystemTypes?.some(
                    (interventionFileSystemType: any) =>
                      interventionFileSystemType?.systemType?.name === 'I.T. general',
                  ) && (
                    <>
                      <Form.Item label='Echipament'>
                        <Input
                          value={equipment}
                          onChange={(e) => setEquipment(e.target.value)}
                          placeholder='Ex: Laptop, Imprimanta'
                        />
                      </Form.Item>

                      <Form.Item label='IP'>
                        <Input value={ip} onChange={(e) => setIp(e.target.value)} placeholder='Ex: 192.168.1.1' />
                      </Form.Item>

                      <Form.Item>
                        {editingIndex === null ? (
                          <Button
                            type='primary'
                            onClick={handleAddEquipment}
                            disabled={!equipment || !ip}
                            style={{ width: '100%' }}
                          >
                            Adaugă Echipament
                          </Button>
                        ) : (
                          <Button
                            type='default'
                            onClick={handleUpdateEquipment}
                            disabled={!equipment || !ip}
                            style={{ width: '100%' }}
                          >
                            Actualizează Echipament
                          </Button>
                        )}
                      </Form.Item>

                      {/* Display equipment-IP pairs */}
                      {reduxInterventionFile?.interventionFileEquipments?.length > 0 && <h3>Echipamente adăugate:</h3>}
                      <div style={{ marginTop: '20px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          {reduxInterventionFile.interventionFileEquipments.map((equip: any, index: any) => (
                            <div
                              key={index}
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                margin: '8px 0',
                                borderBottom: '1px solid #ddd',
                              }}
                            >
                              <span style={{ flex: 1 }}>
                                <strong>{equip.equipment}</strong> - {equip.ip}
                              </span>
                              <Button
                                type='link'
                                icon={<EditOutlined />}
                                onClick={() => handleEditEquipment(index)}
                                style={{ marginRight: 10 }}
                              />
                              <Button
                                type='link'
                                icon={<DeleteOutlined />}
                                danger
                                onClick={() => handleDeleteEquipment(index)}
                              />
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Device selection */}
                      <Form.Item name='device' rules={[{ required: true, message: 'Selectati operatia!' }]}>
                        <Select
                          placeholder='Selectează operația'
                          defaultValue={reduxInterventionFile.device}
                          onChange={handleDeviceChange}
                        >
                          <Select.Option value='Selecteaza device'>Selecteaza device</Select.Option>
                          <Select.Option value='Statie'>Statie</Select.Option>
                          <Select.Option value='Imprimanta'>Imprimanta</Select.Option>
                        </Select>
                      </Form.Item>

                      {/* Operations for Statie */}
                      {reduxInterventionFile.device === 'Statie' && (
                        <>
                          {stationOperations.map((op) => (
                            <Checkbox
                              key={op}
                              checked={reduxInterventionFile.interventionFilePerformOperations.some(
                                (o: any) => o.operation === op && o.isSelected,
                              )}
                              onChange={() => dispatch(updatePerformOperation(op))}
                            >
                              {op}
                            </Checkbox>
                          ))}
                        </>
                      )}

                      {/* Operations for Imprimanta */}
                      {reduxInterventionFile.device === 'Imprimanta' && (
                        <>
                          {printerOperations.map((op) => (
                            <Checkbox
                              key={op}
                              checked={reduxInterventionFile.interventionFilePerformOperations.some(
                                (o: any) => o.operation === op && o.isSelected,
                              )}
                              onChange={() => dispatch(updatePerformOperation(op))}
                            >
                              {op}
                            </Checkbox>
                          ))}
                        </>
                      )}

                      {/* Additional Notes */}
                      <Form.Item name='otherActivities' style={{ marginTop: 15 }}>
                        <Input.TextArea placeholder='Alte Activitati' rows={3} />
                      </Form.Item>
                    </>
                  )}
                </Col>
              </Row>
            </div>

            <Form.Item label='Punctul detine jurnal?' name='hasJournal'>
              <Checkbox
                onChange={(e) => handleCheckBoxChange('hasJournal', e.target.checked)}
                checked={reduxInterventionFile?.hasJournal}
              >
                {reduxInterventionFile?.hasJournal ? 'Da' : 'Nu'}
              </Checkbox>
            </Form.Item>

            <Form.Item name='attachedImage'>
              <Upload {...getUploadProps()} showUploadList={false}>
                <div style={{ display: 'flex', flexWrap: 'wrap', columnGap: '20px' }}>
                  <Button icon={<UploadOutlined />}>Atasati Imagine</Button>
                  {!!initialValues.attachedImage && (
                    <Button type='primary' icon={<DeleteOutlined />} danger onClick={handleDeleteImage}>
                      Stergeti Imagine
                    </Button>
                  )}
                </div>
              </Upload>

              {initialValues.attachedImage && (
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: '10px',
                    marginTop: '10px',
                    marginLeft: '70px',
                  }}
                >
                  <Image width={200} src={`data:image/png;base64,${initialValues.attachedImage}`} alt='Image' />
                </div>
              )}
            </Form.Item>
          </div>
          <hr />
          <div style={{ display: 'flex', flexWrap: 'wrap', columnGap: '20%', justifyContent: 'center' }}>
            <div>
              <h3>Defect semnalat</h3>
              <Form.Item name='reportedDefect' style={{ width: 300 }}>
                <TextArea
                  placeholder='Exemplu: imprimanta defecta'
                  disabled={reduxInterventionFile?.requestFile?.remarks}
                />
              </Form.Item>
            </div>
            <div>
              <h3>Constatare</h3>
              <Form.Item name='ascertainment' style={{ width: 300 }}>
                <TextArea placeholder='Exemplu: drum unit avariat' />
              </Form.Item>
            </div>
          </div>
          <hr />
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-evenly' }}>
            <div>
              <h3>Info Service</h3>
              <Form.Item label='Preluat in service' name='takenIntoService'>
                <Checkbox
                  onChange={(e) => handleCheckBoxChange('takenIntoService', e.target.checked)}
                  checked={reduxInterventionFile?.takenIntoService}
                >
                  {reduxInterventionFile?.takenIntoService ? 'Da' : 'Nu'}
                </Checkbox>
              </Form.Item>
              <Form.Item label='Aparat reparat' name='deviceRepaired'>
                <Checkbox
                  onChange={(e) => handleCheckBoxChange('deviceRepaired', e.target.checked)}
                  checked={reduxInterventionFile?.deviceRepaired}
                >
                  {reduxInterventionFile?.deviceRepaired ? 'Da' : 'Nu'}
                </Checkbox>
              </Form.Item>
              <Form.Item name='estimatedPrice'>
                <InputNumber
                  addonBefore='Pret estimativ'
                  disabled={reduxInterventionFile?.recommendationDetails == ''}
                  placeholder='Ex: 150 lei'
                />
              </Form.Item>
            </div>
            <div style={{ width: 300 }}>
              <h3>Operatii</h3>
              <Form.Item name='operations'>
                <TextArea placeholder='Exemplu: schimbare scanner' />
              </Form.Item>
            </div>

            <div style={{ width: '300px' }}>
              <h3>Consum</h3>
              <Collapse
                accordion
                items={[
                  {
                    key: '1',
                    label: 'Schimbare acumulator',
                    children: (
                      <div>
                        <div>
                          <Form.Item name='isAccumulatorChanged' valuePropName='checked'>
                            <Checkbox
                              onChange={(e) => handleCheckBoxChange('isAccumulatorChanged', e.target.checked)}
                              checked={reduxInterventionFile?.isAccumulatorChanged}
                            >
                              Schimbare acumulatori
                            </Checkbox>
                            <div>Acumulator pentru centrale</div>
                          </Form.Item>
                        </div>
                        <hr />
                        <div>
                          <Form.Item name='isBatteryChanged' valuePropName='checked'>
                            <Checkbox
                              onChange={(e) => handleCheckBoxChange('isBatteryChanged', e.target.checked)}
                              checked={reduxInterventionFile?.isBatteryChanged}
                            >
                              Schimbare baterie
                            </Checkbox>
                            <div>Cand se schimba bateria in senzori</div>
                          </Form.Item>
                        </div>
                        <hr />
                      </div>
                    ),
                  },
                ]}
              />
              <Form.Item name='consumptionDetails'>
                <TextArea placeholder='Exemplu: tonner original xerox' />
              </Form.Item>
            </div>

            <div style={{ width: '300px' }}>
              <h3>Recomandari</h3>
              <Collapse
                accordion
                items={[
                  {
                    key: '2',
                    label: 'Se pot face vanzari',
                    children: (
                      <div>
                        <div>
                          <Form.Item name='canBeUpgradedWithPresales' valuePropName='checked'>
                            <Checkbox
                              onChange={(e) => handleCheckBoxChange('canBeUpgradedWithPresales', e.target.checked)}
                              checked={reduxInterventionFile?.canBeUpgradedWithPresales}
                            >
                              Se poate face upgrade -<b>Necesita vizită Presales</b>
                            </Checkbox>
                            <div>
                              Solutia actuala se poate imbunatati, dar necesita avizarea expertilor din Presales
                            </div>
                          </Form.Item>
                        </div>
                        <hr />
                        <div>
                          <Form.Item name='canBeUpgradedWithoutPresales' valuePropName='checked'>
                            <Checkbox
                              onChange={(e) => handleCheckBoxChange('canBeUpgradedWithoutPresales', e.target.checked)}
                              checked={reduxInterventionFile?.canBeUpgradedWithoutPresales}
                            >
                              Se poate face upgrade
                            </Checkbox>
                            <div>Solutia actuala se poate imbunatati</div>
                          </Form.Item>
                        </div>
                        <hr />
                        <>
                          <span style={{ color: 'red' }}>
                            <b>Obligatoriu:</b>
                          </span>
                          <b>SE NOTEAZA DETALIILE POSIBILEI VANZARI</b>
                          <div>
                            Toate vanzarile finalizate din sugestia tehnicianului vor fi comisionate corespunzator
                          </div>
                        </>
                      </div>
                    ),
                  },
                ]}
              />
              <Form.Item name='recommendationDetails'>
                <TextArea placeholder='Exemplu: upgrade catre un hard disk SDD, un SSD mai rapid.' />
              </Form.Item>
            </div>
          </div>
          <hr />
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-evenly' }}>
            <Form.Item name='helionEngineers' rules={[{ required: true, message: 'Alegeti un inginer!' }]}>
              <>
                <h3>Inginer Helion</h3>
                <div style={{ width: '300px', margin: '0 auto' }}>
                  <Input
                    placeholder='Cauta inginer'
                    value={filterTextTehnician}
                    onChange={(e) => setFilterTextTehnician(e.target.value)}
                    style={{ marginBottom: '10px' }}
                  />
                  <div
                    style={{
                      height: '300px',
                      overflowY: 'auto',
                      border: '1px solid #d9d9d9',
                      borderRadius: '4px',
                    }}
                  >
                    <List
                      dataSource={users.filter((item: any) =>
                        item.name.toLowerCase().includes(filterTextTehnician.toLowerCase()),
                      )}
                      renderItem={(item: any) => (
                        <List.Item style={{ marginLeft: '10%' }}>
                          <Checkbox
                            checked={reduxInterventionFile?.helionEngineers?.some(
                              (engineer: any) => engineer?.userId === item?.id,
                            )}
                            onChange={(e) => onChangeEngineer(item, e.target.checked)}
                          >
                            <>
                              <div>
                                <b>{item.name}</b>
                              </div>
                              <div>Rol: [{item.userPermission?.rols.join(', ')}]</div>
                            </>
                          </Checkbox>
                        </List.Item>
                      )}
                    />
                  </div>
                </div>
              </>
            </Form.Item>
            <div>
              <h3>Semnatura Reprezentant Client</h3>
              <Form.Item name='representativeSignature' style={{ width: '100%', textAlign: 'center' }}>
                <SignaturePad
                  valueSignature={reduxInterventionFile?.representativeSignature}
                  onSignatureDrawn={handleRepresentativeSignatureChange}
                />
              </Form.Item>
            </div>
            <div>
              <h3>Data Predarii</h3>
              <Form.Item
                label='Data Predarii'
                name='deliveryDate'
                rules={[{ required: true, message: 'Alegeti o data!' }]}
              >
                <Input type='date' />
              </Form.Item>
              <Form.Item
                label='Timp transport (spre si de la obiectiv)'
                name='timeTransport'
                rules={[{ required: true, message: 'Introduceti timpul de transport!' }]}
              >
                <InputNumber type='number' addonBefore='Ex:' addonAfter='min' placeholder='20' min={0} />
              </Form.Item>
              <Form.Item
                label='Kilometri parcursi (spre si de la obiectiv)'
                name='traveledKilometers'
                rules={[{ required: true, message: 'Introduceti kilometrii parcursi!' }]}
              >
                <InputNumber type='number' addonBefore='Ex:' addonAfter='km' placeholder='20' min={0} />
              </Form.Item>
              <Form.Item
                label='Timp manopera'
                name='laborTime'
                rules={[{ required: true, message: 'Introduceti timpul de manopera!' }]}
              >
                <InputNumber type='number' addonBefore='Ex:' addonAfter='min' placeholder='20' min={0} />
              </Form.Item>
            </div>
          </div>
          <hr />
          <div>
            <h3>Semnatura Helion</h3>
            <Form.Item
              name='signature'
              style={{ width: 300, textAlign: 'center' }}
              rules={[{ required: true, message: 'Introduceti semnatura!' }]}
            >
              <SignaturePad
                valueSignature={reduxInterventionFile?.signature}
                onSignatureDrawn={handleClientSignatureChange}
              />
            </Form.Item>
            <div style={{ width: '100%' }}>
              <i>
                Prin semnarea prezentei Fise de Service Clientul, prin reprezentantul sau, accepta lucrarile, confirma
                executarea acestora si accepta receptia lor intocmai.
              </i>
            </div>
          </div>
          <hr />
          <h3>Observatii din partea clientului</h3>
          <Form.Item name='contactPersonObservations'>
            <Input />
          </Form.Item>
          <hr />
          <Form.Item className='footer-actions'>
            {isDraft && (
              <div style={{ marginBottom: '10px', color: '#ff4d4f' }}>
                Acesta este o fisa partiala de intervenție. Adăugați semnătura reprezentantului din partea clientului
                pentru a finaliza.
              </div>
            )}
            <Button type='primary' htmlType='submit'>
              {!form.getFieldValue('representativeSignature') ? 'Salveaza partial' : 'Finalizeaza si trimite fisa'}
            </Button>
            <Button danger onClick={handleReset}>
              Reseteaza
            </Button>
          </Form.Item>
        </Form>
      </div>
    </>
  );
};

export default InterventionFilePage;
